import {
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  Row,
} from '@tanstack/react-table';
import { TableProps } from '../types';
import { Box } from '../../box';
import { Grid } from '../../grid';
import { useState, useEffect } from 'react';
import { IndeterminateCheckbox } from '../indeterminate-checkbox';
import { ListTableCell } from './list-table-cell';

type ListTableProps<T> = Omit<TableProps<T>, 'isSortable'>;

export const ListTable = <T,>({
  columns,
  data,
  size = 'normal',
  customCellStyles,
  onSelections,
}: ListTableProps<T>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const tableColumns = onSelections
    ? [
        {
          id: 'select',
          cell: ({ row }: { row: Row<T> }) => (
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          ),
        },
        ...columns,
      ]
    : columns;

  useEffect(() => {
    if (onSelections) {
      onSelections(rowSelection);
    }
  }, [rowSelection, onSelections]);

  const table = useReactTable<T>({
    columns: tableColumns,
    data,
    state: onSelections ? { rowSelection } : undefined,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: !!onSelections,
    onRowSelectionChange: onSelections ? setRowSelection : undefined,
  });

  const rows = table.getRowModel().rows;
  const headers = table.getFlatHeaders();

  return (
    <Grid>
      {onSelections && (
        <Box
          paddingBlock={size === 'normal' ? 4 : 3}
          paddingInline={size === 'normal' ? 2 : 0}
        >
          <IndeterminateCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        </Box>
      )}

      {rows.map((row) => (
        <Grid
          key={row.id}
          borderTopColor="lineBorder"
          borderTopWidth="1px"
          borderTopStyle="solid"
          paddingBlock={size === 'normal' ? 4 : 3}
          gap={size === 'normal' ? 2 : 0}
          paddingInline={size === 'normal' ? 2 : 0}
        >
          {row.getAllCells().map((cell, index) => (
            <ListTableCell<T>
              key={cell.id}
              cell={cell}
              size={size}
              customCellStyles={customCellStyles}
              headers={headers}
              index={index}
            />
          ))}
        </Grid>
      ))}
    </Grid>
  );
};
